import {GraphVariableModel} from "../shared/models/graph-variable.model";

/**
 * #29 — the identity half of the request-validity gate.
 *
 * Both fields are optional. A caller that supplies neither gets a pure completeness check;
 * that is exactly what the standalone `/graph` page needs, and is why this module can be
 * shared without altering `/graph` behaviour (PM correction 1: the Product Page Dataset
 * Charts surface is the only caller that supplies a context).
 */
export interface GraphRequestContext {
  /** The active dataset's `title`, matched against each row's `collectionName`. */
  collectionTitle?: string | null;
  /** The #24 chart-enabled column keys for the active dataset. */
  chartEnabledKeys?: string[] | null;
}

/**
 * A stored row carries strings, numbers (`singleDepth: 0`), `Date` objects and `null`s, so
 * "present" cannot be a plain truthiness test — `0` is a legitimate depth.
 */
function isPresent(value: any): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (value instanceof Date) {
    return !isNaN(value.getTime());
  }
  return `${value}`.trim() !== '';
}

function toTime(value: any): number {
  return new Date(value).getTime();
}

/**
 * Is this single stored/selected row something the graph API can be asked about?
 *
 * The completeness rules generally mirror `SelectGraphForm.validateForm()`
 * (`graph-form/graph-form.component.ts`), which is the gate a user must already pass to
 * submit a selection, so the two do not drift.
 *
 * The one rule that is deliberately **stricter** than the form is the timestep: an
 * aggregated `dataType` requires `timeSteps` in *either* depth mode, whereas
 * `validateForm()` only enforces it in `rangeDepth`. The processor derives its resampling
 * frequency from `timeSteps` alone and applies it whenever `dataType != 'raw'`, with no
 * depth-mode condition (`data-processor-api/routes/graph.py:473-503`): a missing timestep
 * leaves `rsmple = False` and the backend calls `resample(False)`, whose exception is
 * swallowed rather than returned. Mirroring the form's omission here would preserve that
 * undefined backend path instead of validating the request, so the approved #29 rule
 * — `dataType !== 'raw' -> timeSteps present` — is what the gate enforces.
 *
 * The identity rules are additional and apply only when the caller supplies them.
 */
export function isRequestableRow(
  row: GraphVariableModel | null | undefined,
  context: GraphRequestContext = {}
): boolean {
  if (!row) {
    return false;
  }

  // --- completeness (mirrors validateForm) ---

  if (!isPresent(row.column) || !isPresent(row.station)) {
    return false;
  }
  // #29 reopen — `category` is the one field where `null` is requestable. The Dataset
  // Charts form copies `collection.category`, which is `null` for every Admin-created MED
  // collection, and the processor requires only that the serialized key exists — the value
  // is never used to build or run the query. Requiring a non-empty value here silently
  // blocked every MED chart request. `null` therefore passes; a missing/`undefined`
  // category (which `JSON.stringify` would drop, making the backend 400) and the
  // empty/whitespace strings the old rule rejected all stay fail-closed.
  const categoryIsRequestable = row.category === null || isPresent(row.category);
  if (!isPresent(row.collectionName) || !categoryIsRequestable) {
    return false;
  }

  if (!isPresent(row.fromDate) || !isPresent(row.toDate)) {
    return false;
  }
  const fromTime = toTime(row.fromDate);
  const toTimeValue = toTime(row.toDate);
  if (isNaN(fromTime) || isNaN(toTimeValue) || fromTime > toTimeValue) {
    return false;
  }

  if (!isPresent(row.dataType)) {
    return false;
  }
  // Depth-mode independent, matching how the processor actually resamples — see the note
  // on this function.
  if (row.dataType !== 'raw' && !isPresent(row.timeSteps)) {
    return false;
  }

  if (row.depthMode === 'singleDepth') {
    if (!isPresent(row.singleDepth)) {
      return false;
    }
  } else if (row.depthMode === 'rangeDepth') {
    if (!isPresent(row.fromDepth) || !isPresent(row.toDepth)) {
      return false;
    }
    if (!isPresent(row.depthAggregation)) {
      return false;
    }
  } else {
    // Missing or unrecognised depth mode — the form always writes one of the two above.
    return false;
  }

  // --- identity (Product Page Dataset Charts only) ---

  // A Product Page selection is stored under one context-scoped key shared by every
  // dataset, so a row left over from another dataset is still in storage when this one
  // opens. It is stale state, not a request.
  if (isPresent(context.collectionTitle) && row.collectionName !== context.collectionTitle) {
    return false;
  }

  // #24 — a variable that is not chart-enabled for the active dataset must not come back
  // in through restored state. An empty list is a legitimate "nothing is eligible".
  if (context.chartEnabledKeys !== null && context.chartEnabledKeys !== undefined) {
    if (!context.chartEnabledKeys.includes(row.column)) {
      return false;
    }
  }

  return true;
}

/**
 * The requestable subset of a selection, in its original order. Non-array input yields an
 * empty array so callers never have to null-check first.
 */
export function filterRequestableRows(
  rows: GraphVariableModel[] | null | undefined,
  context: GraphRequestContext = {}
): GraphVariableModel[] {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.filter(row => isRequestableRow(row, context));
}
